
import React, { useState, useEffect, useRef } from 'react';
import { chatService } from '../services/chatService';
import { Conversation, Message } from '../types';
import { Send, User, Search, MessageSquare, Loader2, Info } from 'lucide-react';

interface AdminMessagesProps {
  storeId: string;
}

const AdminMessages: React.FC<AdminMessagesProps> = ({ storeId }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [reply, setReply] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadChats = () => {
      const convs = chatService.getStoreConversations(storeId);
      setConversations(convs);
      if (selectedConv) {
        const updated = convs.find(c => c.id === selectedConv.id);
        if (updated) setSelectedConv(updated);
      }
    };

    loadChats();
    return chatService.onChatUpdate(loadChats);
  }, [storeId, selectedConv?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedConv?.messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reply.trim() || !selectedConv) return;

    await chatService.sendMessage(storeId, selectedConv.customerId, reply);
    setReply('');
  };

  return (
    <div className="h-[calc(100vh-160px)] flex bg-white rounded-[40px] border border-gray-100 overflow-hidden shadow-xl shadow-gray-100/50">
      {/* Sidebar: Conversation List */}
      <div className="w-80 border-r border-gray-50 flex flex-col">
        <div className="p-6 border-b border-gray-50">
          <h2 className="text-xl font-black text-gray-900 mb-4">Inbox</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
            <input 
              type="text" 
              placeholder="Search chats..."
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-2xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="flex-grow overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-10 text-center space-y-2 opacity-30">
               <MessageSquare className="h-10 w-10 mx-auto" />
               <p className="text-xs font-bold">No messages yet.</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <button 
                key={conv.id}
                onClick={() => setSelectedConv(conv)}
                className={`w-full p-6 text-left border-b border-gray-50 hover:bg-gray-50 transition-all flex items-center gap-4 ${selectedConv?.id === conv.id ? 'bg-blue-50/50 border-l-4 border-l-blue-600' : ''}`}
              >
                 <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center shrink-0">
                    <User className="h-6 w-6 text-gray-400" />
                 </div>
                 <div className="min-w-0 flex-grow">
                    <div className="flex justify-between items-center mb-1">
                       <p className="font-black text-sm text-gray-900 truncate">{conv.customerId}</p>
                       <p className="text-[8px] font-bold text-gray-400 uppercase">
                         {new Date(conv.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                       </p>
                    </div>
                    <p className="text-xs text-gray-400 truncate font-medium">
                      {conv.messages[conv.messages.length - 1].text}
                    </p>
                 </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConv ? (
        <div className="flex-grow flex flex-col">
          {/* Top Bar */}
          <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
             <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center border border-gray-100">
                  <User className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                   <h3 className="font-black text-gray-900">{selectedConv.customerId}</h3>
                   <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer Online</p>
                   </div>
                </div>
             </div>
             <button className="p-3 text-gray-400 hover:bg-gray-100 rounded-xl transition-all">
                <Info className="h-5 w-5" />
             </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-grow p-10 overflow-y-auto space-y-6 bg-gray-50/30">
             {selectedConv.messages.map((msg) => (
               <div key={msg.id} className={`flex ${msg.senderId === storeId ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[60%] p-5 rounded-[32px] text-sm font-medium shadow-sm ${
                    msg.senderId === storeId 
                    ? 'bg-blue-600 text-white rounded-tr-none' 
                    : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                  }`}>
                    {msg.text}
                    <p className={`text-[8px] mt-2 font-black uppercase tracking-tighter opacity-50 ${msg.senderId === storeId ? 'text-white' : 'text-gray-400'}`}>
                       {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
               </div>
             ))}
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="p-8 border-t border-gray-50 bg-white">
             <div className="relative">
                <input 
                  type="text" 
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your message to the customer..."
                  className="w-full pl-8 pr-16 py-5 bg-gray-50 border-none rounded-[2rem] focus:ring-2 focus:ring-blue-500 font-bold"
                />
                <button 
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-4 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all active:scale-95 shadow-xl shadow-blue-100"
                >
                  <Send className="h-5 w-5" />
                </button>
             </div>
          </form>
        </div>
      ) : (
        <div className="flex-grow flex flex-col items-center justify-center space-y-6 bg-gray-50/20">
           <div className="w-24 h-24 bg-blue-50 rounded-[2.5rem] flex items-center justify-center text-blue-600">
              <MessageSquare className="h-10 w-10" />
           </div>
           <div className="text-center space-y-2">
              <h3 className="text-2xl font-black text-gray-900">Select a Conversation</h3>
              <p className="text-gray-400 font-medium">Choose a customer to start chatting live.</p>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
